'use client';
import { useEffect, useRef, useState } from 'react';
import type { DesignPublishedSnapshot } from '@open-design/contracts';
import { platform } from './api';
import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from './ui';
import css from './PersistentEditor.module.css';

export function PublishedVersions({
  websiteId,
  org,
  dirty,
  blocked,
  error,
  onClose,
  onSave,
  onDiscard,
  onRestore,
}: {
  websiteId: string;
  org: number;
  dirty: boolean;
  blocked: boolean;
  error: string;
  onClose: () => void;
  onSave: () => void;
  onDiscard: () => void;
  onRestore: (releaseId: string) => Promise<boolean>;
}) {
  const [snapshots, setSnapshots] = useState<DesignPublishedSnapshot[] | null>(
    null,
  );
  const [selected, setSelected] = useState('');
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    root.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => {
      if (before?.isConnected) before.focus();
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoadError('');
    void platform<DesignPublishedSnapshot[]>(
      `websites/${websiteId}/publications/`,
      { org, signal: controller.signal },
    )
      .then(setSnapshots)
      .catch((cause) => {
        if (!controller.signal.aborted)
          setLoadError(
            cause instanceof Error
              ? cause.message
              : 'Could not load published versions.',
          );
      });
    return () => controller.abort();
  }, [websiteId, org, retry]);
  return (
    <Dialog
      ariaLabel="Restore previous version"
      onClose={onClose}
      closeOnEscape
    >
      <div
        ref={root}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const nodes = Array.from(
            root.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled),a[href]',
            ) ?? [],
          );
          const target = event.shiftKey ? nodes.at(-1) : nodes[0];
          if (
            document.activeElement ===
            (event.shiftKey ? nodes[0] : nodes.at(-1))
          ) {
            event.preventDefault();
            target?.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Restore previous version</DialogTitle>
          <Button aria-label="Close published versions" onClick={onClose}>
            ×
          </Button>
        </DialogHeader>
        <DialogBody>
          <p>
            Choose a published snapshot to restore to your working draft. Your
            live website will stay unchanged until you publish.
          </p>
          {loadError ? (
            <p role="alert">
              {loadError}{' '}
              <Button onClick={() => setRetry((value) => value + 1)}>
                Retry
              </Button>
            </p>
          ) : snapshots === null ? (
            <p role="status">Loading published versions…</p>
          ) : !snapshots.length ? (
            <p>No published versions yet.</p>
          ) : (
            <div className={css.publications}>
              {snapshots.map((item) => (
                <div
                  key={item.release_id}
                  className={selected === item.release_id ? css.selected : ''}
                >
                  <Button
                    aria-pressed={selected === item.release_id}
                    onClick={() => setSelected(item.release_id)}
                    disabled={blocked}
                  >
                    {new Date(item.published_at).toLocaleString()}{' '}
                    {item.is_live && <small>Current live version</small>}
                  </Button>
                  {item.review_url && (
                    <a href={item.review_url} target="_blank" rel="noreferrer">
                      Preview ↗
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          {dirty && (
            <div role="status">
              Save or discard your edits before restoring.
              <div className={css.actions}>
                <Button disabled={blocked} onClick={onSave}>
                  Save changes
                </Button>
                <Button disabled={blocked} onClick={onDiscard}>
                  Discard changes
                </Button>
              </div>
            </div>
          )}
          {selected && !dirty && (
            <p>
              Restore this snapshot? It will replace the working draft and run
              checks before updating your preview.
            </p>
          )}
          {error && <p role="alert">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            className={css.primary}
            disabled={!selected || blocked || dirty || !!loadError}
            onClick={async () => {
              if (await onRestore(selected)) onClose();
            }}
          >
            Restore to draft
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}
